import { supabase } from '../supabaseClient'

export const rhuService = {
  // ==================== CATEGORIES ====================
  
  async getAllCategories() {
    try {
      const { data, error } = await supabase
        .from('rhu_categories')
        .select('*')
        .order('name', { ascending: true })
      
      if (error) throw error
      return { success: true, data: data || [] }
    } catch (error) {
      console.error('Error fetching categories:', error)
      return { success: false, error: error.message }
    }
  },

  async addCategory(categoryData) {
    try {
      const { data, error } = await supabase
        .from('rhu_categories')
        .insert([{ name: categoryData.name }])
        .select()
      
      if (error) throw error

      return { success: true, data: data[0] }
    } catch (error) {
      console.error('Error adding category:', error)
      return { success: false, error: error.message }
    }
  },

  async updateCategory(categoryId, categoryData) {
    try {
      const { data, error } = await supabase
        .from('rhu_categories')
        .update({ name: categoryData.name })
        .eq('id', categoryId)
        .select()
      
      if (error) throw error

      return { success: true, data: data[0] }
    } catch (error) {
      console.error('Error updating category:', error)
      return { success: false, error: error.message }
    }
  },

  async deleteCategory(categoryId) {
    try {
      // Delete all subcategories first (this will cascade delete services)
      await supabase
        .from('rhu_subcategories')
        .delete()
        .eq('category_id', categoryId)

      // Then delete the category
      const { error } = await supabase
        .from('rhu_categories')
        .delete()
        .eq('id', categoryId)
      
      if (error) throw error

      return { success: true }
    } catch (error) {
      console.error('Error deleting category:', error)
      return { success: false, error: error.message }
    }
  },

  // ==================== SUBCATEGORIES ====================
  
  async getSubcategoriesByCategory(categoryId) {
    try {
      const { data, error } = await supabase
        .from('rhu_subcategories')
        .select('*')
        .eq('category_id', categoryId)
        .order('name', { ascending: true })
      
      if (error) throw error
      return { success: true, data: data || [] }
    } catch (error) {
      console.error('Error fetching subcategories:', error)
      return { success: false, error: error.message }
    }
  },

  async addSubcategory(subcategoryData) {
    try {
      const { data, error } = await supabase
        .from('rhu_subcategories')
        .insert([{
          category_id: subcategoryData.category_id,
          name: subcategoryData.name
        }])
        .select()
      
      if (error) throw error

      return { success: true, data: data[0] }
    } catch (error) {
      console.error('Error adding subcategory:', error)
      return { success: false, error: error.message }
    }
  },

  async updateSubcategory(subcategoryId, subcategoryData) {
    try {
      const { data, error } = await supabase
        .from('rhu_subcategories')
        .update({ name: subcategoryData.name })
        .eq('id', subcategoryId)
        .select()
      
      if (error) throw error

      return { success: true, data: data[0] }
    } catch (error) {
      console.error('Error updating subcategory:', error)
      return { success: false, error: error.message }
    }
  },

  async deleteSubcategory(subcategoryId) {
    try {
      // Delete all services in this subcategory first
      await supabase
        .from('rhu_services')
        .delete()
        .eq('subcategory_id', subcategoryId)

      // Then delete the subcategory
      const { error } = await supabase
        .from('rhu_subcategories')
        .delete()
        .eq('id', subcategoryId)
      
      if (error) throw error

      return { success: true }
    } catch (error) {
      console.error('Error deleting subcategory:', error)
      return { success: false, error: error.message }
    }
  },

  // ==================== SERVICES ====================
  
  async getServicesBySubcategory(subcategoryId) {
    try {
      const { data, error } = await supabase
        .from('rhu_services')
        .select('*')
        .eq('subcategory_id', subcategoryId)
        .order('name', { ascending: true })
      
      if (error) throw error
      return { success: true, data: data || [] }
    } catch (error) {
      console.error('Error fetching services:', error)
      return { success: false, error: error.message }
    }
  },

  async addService(serviceData) {
    try {
      const { data, error } = await supabase
        .from('rhu_services')
        .insert([{
          subcategory_id: serviceData.subcategory_id,
          name: serviceData.name,
          price: serviceData.price,
          description: serviceData.description
        }])
        .select()
      
      if (error) throw error

      return { success: true, data: data[0] }
    } catch (error) {
      console.error('Error adding service:', error)
      return { success: false, error: error.message }
    }
  },

  async updateService(serviceId, serviceData) {
    try {
      const { data, error } = await supabase
        .from('rhu_services')
        .update({
          name: serviceData.name,
          price: serviceData.price,
          description: serviceData.description
        })
        .eq('id', serviceId)
        .select()
      
      if (error) throw error

      return { success: true, data: data[0] }
    } catch (error) {
      console.error('Error updating service:', error)
      return { success: false, error: error.message }
    }
  },

  async deleteService(serviceId) {
    try {
      const { error } = await supabase
        .from('rhu_services')
        .delete()
        .eq('id', serviceId)
      
      if (error) throw error
      return { success: true }
    } catch (error) {
      console.error('Error deleting service:', error)
      return { success: false, error: error.message }
    }
  },

  // ==================== COMBINED DATA ====================
  
  async getAllServicesHierarchy() {
    try {
      // Get all categories
      const { data: categories, error: catError } = await supabase
        .from('rhu_categories')
        .select('*')
        .order('name', { ascending: true })
      
      if (catError) throw catError

      // Get all subcategories
      const { data: subcategories, error: subError } = await supabase
        .from('rhu_subcategories')
        .select('*')
        .order('name', { ascending: true })
      
      if (subError) throw subError

      // Get all services
      const { data: services, error: srvError } = await supabase
        .from('rhu_services')
        .select('*')
        .order('name', { ascending: true })
      
      if (srvError) throw srvError

      // Build hierarchy
      const hierarchy = categories.map(category => ({
        id: category.id,
        name: category.name,
        subcategories: subcategories
          .filter(sub => sub.category_id === category.id)
          .map(sub => ({
            id: sub.id,
            name: sub.name,
            services: services
              .filter(srv => srv.subcategory_id === sub.id)
              .map(srv => ({
                id: srv.id,
                name: srv.name,
                price: parseFloat(srv.price),
                description: srv.description
              }))
          }))
      }))

      return { success: true, data: hierarchy }
    } catch (error) {
      console.error('Error fetching services hierarchy:', error)
      return { success: false, error: error.message }
    }
  },

  // ==================== PATIENTS ====================
  
  async getAllPatients() {
    try {
      const { data, error } = await supabase
        .from('rhu_patients')
        .select('*')
        .order('name', { ascending: true })
      
      if (error) throw error
      return { success: true, data: data || [] }
    } catch (error) {
      console.error('Error fetching patients:', error)
      return { success: false, error: error.message }
    }
  },

  async searchPatients(searchTerm) {
    try {
      const { data, error } = await supabase
        .from('rhu_patients')
        .select('*')
        .ilike('name', `%${searchTerm}%`)
        .order('name', { ascending: true })
        .limit(10)
      
      if (error) throw error
      return { success: true, data: data || [] }
    } catch (error) {
      console.error('Error searching patients:', error)
      return { success: false, error: error.message }
    }
  },

  async addPatient(patientData) {
    try {
      const { data, error } = await supabase
        .from('rhu_patients')
        .insert([{
          name: patientData.name,
          birthdate: patientData.birthdate,
          age: patientData.age,
          address: patientData.address
        }])
        .select()
      
      if (error) throw error

      return { success: true, data: data[0] }
    } catch (error) {
      console.error('Error adding patient:', error)
      return { success: false, error: error.message }
    }
  },

  async updatePatient(patientId, patientData) {
    try {
      const { data, error } = await supabase
        .from('rhu_patients')
        .update({
          name: patientData.name,
          birthdate: patientData.birthdate,
          age: patientData.age,
          address: patientData.address
        })
        .eq('id', patientId)
        .select()
      
      if (error) throw error

      return { success: true, data: data[0] }
    } catch (error) {
      console.error('Error updating patient:', error)
      return { success: false, error: error.message }
    }
  },

  // ==================== ORDERS ====================
  
  async getAllOrders() {
    try {
      const { data, error } = await supabase
        .from('rhu_orders')
        .select(`
          *,
          patient:rhu_patients(id, name, birthdate, age, address),
          items:rhu_order_items(*)
        `)
        .order('created_at', { ascending: false })
      
      if (error) throw error
      return { success: true, data: data || [] }
    } catch (error) {
      console.error('Error fetching orders:', error)
      return { success: false, error: error.message }
    }
  },

  async getOrderWithItems(orderId) {
    try {
      const { data: order, error: orderError } = await supabase
        .from('rhu_orders')
        .select(`
          *,
          patient:rhu_patients(id, name, birthdate, age, address)
        `)
        .eq('id', orderId)
        .single()
      
      if (orderError) throw orderError

      const { data: items, error: itemsError } = await supabase
        .from('rhu_order_items')
        .select(`
          *,
          service:rhu_services(
            subcategory:rhu_subcategories(
              name,
              category:rhu_categories(name)
            )
          )
        `)
        .eq('order_id', orderId)
      
      if (itemsError) throw itemsError

      // Enrich items with category/subcategory data if not already present
      const enrichedItems = items.map(item => {
        // Use stored data if available, otherwise fall back to service lookup
        const category = item.category || item.service?.subcategory?.category?.name || ''
        const subcategory = item.subcategory || item.service?.subcategory?.name || ''
        
        return {
          ...item,
          category,
          subcategory
        }
      })

      return { 
        success: true, 
        data: { 
          ...order, 
          items: enrichedItems || [] 
        } 
      }
    } catch (error) {
      console.error('Error fetching order with items:', error)
      return { success: false, error: error.message }
    }
  },

  async createOrder(orderData) {
    try {
      // Generate order number
      const orderNumber = `RHU-${Date.now()}`

      // Calculate total
      const total = orderData.items.reduce((sum, item) => {
        return sum + (item.unit_price * item.quantity)
      }, 0)

      // Insert order
      const { data: order, error: orderError } = await supabase
        .from('rhu_orders')
        .insert([{
          patient_id: orderData.patient_id,
          order_number: orderNumber,
          total_amount: total,
          status: 'pending'
        }])
        .select()
        .single()
      
      if (orderError) throw orderError

      // Insert order items
      const items = orderData.items.map(item => ({
        order_id: order.id,
        service_id: item.service_id,
        service_name: item.service_name,
        category: item.category || null,
        subcategory: item.subcategory || null,
        quantity: item.quantity,
        unit_price: item.unit_price,
        subtotal: item.unit_price * item.quantity
      }))

      const { error: itemsError } = await supabase
        .from('rhu_order_items')
        .insert(items)
      
      if (itemsError) throw itemsError

      return { success: true, data: order }
    } catch (error) {
      console.error('Error creating order:', error)
      return { success: false, error: error.message }
    }
  },

  async updateOrder(orderId, orderData) {
    try {
      // Calculate total
      const total = orderData.items.reduce((sum, item) => {
        return sum + (item.unit_price * item.quantity)
      }, 0)

      // Update order total
      const { error: orderError } = await supabase
        .from('rhu_orders')
        .update({ total_amount: total })
        .eq('id', orderId)
      
      if (orderError) throw orderError

      // Delete existing order items
      const { error: deleteError } = await supabase
        .from('rhu_order_items')
        .delete()
        .eq('order_id', orderId)
      
      if (deleteError) throw deleteError

      // Insert updated order items
      const items = orderData.items.map(item => ({
        order_id: orderId,
        service_id: item.service_id,
        service_name: item.service_name,
        category: item.category || null,
        subcategory: item.subcategory || null,
        quantity: item.quantity,
        unit_price: item.unit_price,
        subtotal: item.unit_price * item.quantity
      }))

      const { error: itemsError } = await supabase
        .from('rhu_order_items')
        .insert(items)
      
      if (itemsError) throw itemsError

      return { success: true, data: { id: orderId } }
    } catch (error) {
      console.error('Error updating order:', error)
      return { success: false, error: error.message }
    }
  },

  async updateOrderStatus(orderId, status, orNumber = null, processedBy = null) {
    try {
      const updateData = { status }
      
      // If status is paid, add OR number, payment timestamp, and processed by
      if (status === 'paid' && orNumber) {
        updateData.or_number = orNumber
        updateData.paid_at = new Date().toISOString()
        if (processedBy) {
          updateData.processed_by = processedBy
        }
      }
      
      const { data, error } = await supabase
        .from('rhu_orders')
        .update(updateData)
        .eq('id', orderId)
        .select()
      
      if (error) throw error
      return { success: true, data: data[0] }
    } catch (error) {
      console.error('Error updating order status:', error)
      return { success: false, error: error.message }
    }
  },

  async deleteOrder(orderId) {
    try {
      // Delete order items first
      await supabase
        .from('rhu_order_items')
        .delete()
        .eq('order_id', orderId)

      // Then delete the order
      const { error } = await supabase
        .from('rhu_orders')
        .delete()
        .eq('id', orderId)
      
      if (error) throw error
      return { success: true }
    } catch (error) {
      console.error('Error deleting order:', error)
      return { success: false, error: error.message }
    }
  },

  // ==================== SEARCH SERVICES ====================
  
  async searchServices(searchTerm) {
    try {
      // Fetch all services with their categories and subcategories
      const { data, error } = await supabase
        .from('rhu_services')
        .select(`
          *,
          subcategory:rhu_subcategories(
            id,
            name,
            category:rhu_categories(id, name)
          )
        `)
        .order('name', { ascending: true })
      
      if (error) throw error
      
      // Filter results to include matches in service name, subcategory name, or category name
      const searchLower = searchTerm.toLowerCase().trim()
      const filteredData = data.filter(service => {
        const serviceName = service.name?.toLowerCase() || ''
        const subcategoryName = service.subcategory?.name?.toLowerCase() || ''
        const categoryName = service.subcategory?.category?.name?.toLowerCase() || ''
        
        return serviceName.includes(searchLower) || 
               subcategoryName.includes(searchLower) || 
               categoryName.includes(searchLower)
      })
      
      // Limit to 20 results
      const limitedData = filteredData.slice(0, 20)
      
      return { success: true, data: limitedData }
    } catch (error) {
      console.error('Error searching services:', error)
      return { success: false, error: error.message }
    }
  },

  // ==================== ACCESS CODES ====================
  
  async verifyAccessCode(code) {
    try {
      const { data, error } = await supabase
        .from('rhu_access_codes')
        .select('*')
        .eq('code', code.trim())
        .eq('is_active', true)
        .single()
      
      if (error) {
        if (error.code === 'PGRST116') {
          // No rows returned
          return { success: false, error: 'Invalid access code' }
        }
        throw error
      }

      // Update last used timestamp and use count
      await supabase
        .from('rhu_access_codes')
        .update({ 
          last_used_at: new Date().toISOString(),
          use_count: (data.use_count || 0) + 1
        })
        .eq('id', data.id)

      return { success: true, data }
    } catch (error) {
      console.error('Error verifying access code:', error)
      return { success: false, error: error.message }
    }
  },

  async updateAccessCode(currentCode, newCode) {
    try {
      // First verify current code exists and is active
      const { data: existingCode, error: verifyError } = await supabase
        .from('rhu_access_codes')
        .select('*')
        .eq('code', currentCode.trim())
        .eq('is_active', true)
        .single()
      
      if (verifyError) {
        return { success: false, error: 'Current code not found' }
      }

      // Check if new code already exists
      const { data: duplicateCheck } = await supabase
        .from('rhu_access_codes')
        .select('id')
        .eq('code', newCode.trim())
        .single()
      
      if (duplicateCheck) {
        return { success: false, error: 'New code already exists. Please choose a different code.' }
      }

      // Update the code
      const { error: updateError } = await supabase
        .from('rhu_access_codes')
        .update({ 
          code: newCode.trim(),
          use_count: 0,
          last_used_at: null
        })
        .eq('id', existingCode.id)
      
      if (updateError) throw updateError

      return { success: true }
    } catch (error) {
      console.error('Error updating access code:', error)
      return { success: false, error: error.message }
    }
  }
}
